import _ from 'lodash';
import { BoxProps, lighten, Theme } from '@mui/material';
import { FormApi } from 'final-form';

import {
  Audience_Status,
  AudienceCommunicationPreferenceCriteriaInput,
  AudienceFuelAndConvenienceProfileCriteria,
  AudiencePointBalanceTargetingAttributeInput,
  AudiencePurchasedAtLeastTargetingAttributeFragment,
  AudiencePurchasedAtMostTargetingAttributeFragment,
  AudienceStoreLocationTargetingAttributeInput,
  AudienceTargetingAttributeFragment,
  AudienceTargetingAttributesInput,
  AudienceWalletSteeringTargetingAttributeInput,
  Communication_Preference_Mechanism,
  Count_Products_By,
  Fuel_And_Convenience_Profile_Attribute as Attribute,
  Fuel_And_Convenience_Profile_Attribute,
  Fuel_And_Convenience_Profile_Operator as Operator,
  Fuel_And_Convenience_Profile_Operator,
  Retailer_Network_User_Type_Operator,
  Unit_Of_Measure,
} from '@occc/graphql';
import { TargetingAttributesFormField } from '@occc-app/routes/Audience/constants';

import { grey } from '@mui/material/colors';
import { IS_REQUIRED } from '@occc-app/lib/constants/validationMessages';
import { z } from 'zod';
import {
  AudienceFormValues,
  ExternalIdentityVerificationFormValue,
  OfferFulfillment,
  QualifiedEventsFormValues,
  QualifiedEventType,
  TargetingAttributesFormValues,
  TransactionTimePeriod,
} from './types';
import { productInterestsTargetingAttributeMap } from './components/ProductInterests/constants';
import {
  hasFilledValuesInLoyaltyEventForm,
  hasFilledValuesInPurchaseForm,
} from './components/QualifiedEvents/utils';
import {
  getIsSurveysSectionFilled,
  getSurveyResponseTargetingAttributes,
} from './components/Surveys/utils';
import { convertQualifiedEventsFormValuesToTargetingAttributeInput } from './components/QualifiedEvents/convertQualifiedEventsFormValuesToTargetingAttributeInput';
import { FuelAndConvenienceProfileValues } from './components/FuelAndConvenienceProfile/types';
import { PointBalanceValues } from './components/types';
import { TransactionsMemberPurchasedOperator } from './components/Purchases/constants';
import { mapExternalIdentityVerificationToArray } from './components/ExternalIdentityVerification/utils';

const prepareBehaviorsCriteria = (behaviors?: FuelAndConvenienceProfileValues) => {
  const behaviorsCriteria: Omit<AudienceFuelAndConvenienceProfileCriteria, '__typename'>[] = [];

  if (behaviors?.acquisitionChannelAttribute?.length) {
    behaviorsCriteria.push({
      attribute: Attribute.AcquisitionChannel,
      operator: Operator.IsIn,
      target: behaviors.acquisitionChannelAttribute,
    });
  }

  if (behaviors?.activeSubBrand) {
    behaviorsCriteria.push({
      attribute: Attribute.ActiveSubBrand,
      operator: Operator.IsEqualTo,
      target: [behaviors.activeSubBrand],
    });
  }

  if (behaviors?.hasACH) {
    behaviorsCriteria.push({
      attribute: Attribute.HasAch,
      operator: Fuel_And_Convenience_Profile_Operator.IsEqualTo,
      target: [behaviors.hasACH],
    });
  }

  if (behaviors?.eaivAttributes?.length && behaviors.eaivVerified) {
    const eaivCriteria: Omit<AudienceFuelAndConvenienceProfileCriteria, '__typename'> = {
      attribute: Fuel_And_Convenience_Profile_Attribute.RestrictionVerification,
      operator: behaviors.eaivVerified,
      target: behaviors.eaivAttributes,
    };

    behaviorsCriteria.push(eaivCriteria);
  }

  return behaviorsCriteria;
};

const getPointBalanceTargetingAttribute = (
  pointBalance: PointBalanceValues | undefined,
): AudiencePointBalanceTargetingAttributeInput | undefined => {
  if (
    !pointBalance?.operator ||
    typeof pointBalance?.points !== 'number' ||
    !pointBalance?.period
  ) {
    return;
  }

  if (pointBalance.operator === 'BETWEEN') {
    return {
      minAmount: pointBalance.points,
      maxAmount: pointBalance.toPoints,
      target: pointBalance.period,
    };
  }

  return {
    operator: pointBalance.operator,
    amount: pointBalance.points,
    target: pointBalance.period,
  };
};

export const prepareAudienceTargetingAttributesInput = ({
  ageWithin,
  behaviors,
  purchases,
  segments,
  fulfillments,
  homeStore,
  walletSteeringItems,
  productInterests,
  qualifiedEvents,
  lastVisit,
  pointBalance,
  membershipCreated,
  employeeTargetingAttributes,
  surveys,
  combinedAudiences,
  externalIdentityVerification,
  communicationPreferences,
}: TargetingAttributesFormValues): AudienceTargetingAttributesInput => {
  const targetingAttributesInput: AudienceTargetingAttributesInput = {};

  if (ageWithin?.minimumAge || ageWithin?.maximumAge) {
    targetingAttributesInput.ageWithinTargetingAttribute = ageWithin;
  }

  if (lastVisit && typeof lastVisit.amount === 'number' && lastVisit.operator && lastVisit.period) {
    targetingAttributesInput.timeSinceLastVisitTargetingAttribute = {
      amount: lastVisit.amount,
      operator: lastVisit.operator,
      period: lastVisit.period,
    };
  }

  if (
    membershipCreated &&
    typeof membershipCreated.amount === 'number' &&
    membershipCreated.operator
  ) {
    targetingAttributesInput.timeSinceMembershipAcquisitionTargetingAttribute = {
      amount: membershipCreated.amount,
      operator: membershipCreated.operator,
    };
  }

  targetingAttributesInput.pointBalanceTargetingAttribute =
    getPointBalanceTargetingAttribute(pointBalance);

  if (employeeTargetingAttributes?.retailerNetworkUserType) {
    targetingAttributesInput.audienceRetailerNetworkSiteUsersTargetingAttribute = {
      retailerNetworkUserTypes: [employeeTargetingAttributes.retailerNetworkUserType],
      userTypeOperator: Retailer_Network_User_Type_Operator.Is,
    };
  }

  if (
    employeeTargetingAttributes?.retailerNetworks &&
    targetingAttributesInput.audienceRetailerNetworkSiteUsersTargetingAttribute
  ) {
    targetingAttributesInput.audienceRetailerNetworkSiteUsersTargetingAttribute.retailerNetworkOperator =
      employeeTargetingAttributes.operator || null;
    targetingAttributesInput.audienceRetailerNetworkSiteUsersTargetingAttribute.retailerNetworkUuids =
      employeeTargetingAttributes.retailerNetworks.map(({ uuid }) => uuid);
  }

  const behaviorsCriteria = prepareBehaviorsCriteria(behaviors);

  if (behaviorsCriteria.length) {
    _.set(
      targetingAttributesInput,
      'fuelAndConvenienceProfileTargetingAttribute.criteria',
      behaviorsCriteria,
    );
  }

  if (purchases?.length) {
    const filteredPurchases = purchases.filter(
      (item) => item.threshold && item.productGroups?.length,
    );

    const {
      MoreThan = [],
      MoreOrEqualTo = [],
      LessThan = [],
      LessThanOrEqualTo = [],
    } = _.groupBy(filteredPurchases, 'type');

    const AudiencePurchasedAtLeastTargetingAttribute = [...MoreThan, ...MoreOrEqualTo];
    if (AudiencePurchasedAtLeastTargetingAttribute?.length) {
      targetingAttributesInput.purchasedAtLeastTargetingAttribute =
        AudiencePurchasedAtLeastTargetingAttribute.map(
          ({ productGroups, locations, locationGroups, ...rest }) => {
            const { timePeriodType, ...filteredRest } = rest;

            return {
              ...filteredRest,
              productGroupIds: _.map(productGroups, 'id'),
              locationsIds: locations?.map((location) => location.uuid),
              locationGroupUuids: locationGroups?.map((locationGroup) => locationGroup.uuid),
              excludeThreshold: filteredRest.type === TransactionsMemberPurchasedOperator.MoreThan,
              timeWindow:
                timePeriodType === TransactionTimePeriod.RollingWindow
                  ? rest.timeWindow
                  : undefined,
              relativeMonthsBack:
                timePeriodType === TransactionTimePeriod.PreviousMonth ? 1 : undefined,
              targetMonth:
                timePeriodType === TransactionTimePeriod.FixedMonth && rest.fixedMonth
                  ? rest.fixedMonth.getMonth() + 1
                  : undefined,
              targetYear:
                timePeriodType === TransactionTimePeriod.FixedMonth
                  ? rest.fixedMonth?.getFullYear()
                  : undefined,
              startDate:
                timePeriodType === TransactionTimePeriod.FixedWindow
                  ? rest.fixedWindow?.startDate?.toISOString()
                  : undefined,
              endDate:
                timePeriodType === TransactionTimePeriod.FixedWindow
                  ? rest.fixedWindow?.endDate?.toISOString()
                  : undefined,
              // We remove temporary technical fields to match the schema.
              includeThreshold: undefined,
              type: undefined,
              purchasedLocationUuids: undefined,
              purchasedLocationGroupUuids: undefined,
              fixedWindow: undefined,
              fixedMonth: undefined,
            };
          },
        );
    }
    const AudiencePurchasedAtMostTargetingAttribute = [...LessThan, ...LessThanOrEqualTo];
    if (AudiencePurchasedAtMostTargetingAttribute?.length) {
      targetingAttributesInput.purchasedAtMostTargetingAttribute =
        AudiencePurchasedAtMostTargetingAttribute.map(
          ({ productGroups, locations, locationGroups, ...rest }) => {
            const { timePeriodType, ...filteredRest } = rest;

            return {
              ...filteredRest,
              productGroupIds: _.map(productGroups, 'id'),
              locationsIds: locations?.map((location) => location.uuid),
              locationGroupUuids: locationGroups?.map((locationGroup) => locationGroup.uuid),
              excludeThreshold: filteredRest.type === TransactionsMemberPurchasedOperator.LessThan,
              timeWindow:
                timePeriodType === TransactionTimePeriod.RollingWindow
                  ? rest.timeWindow
                  : undefined,
              relativeMonthsBack:
                timePeriodType === TransactionTimePeriod.PreviousMonth ? 1 : undefined,
              targetMonth:
                timePeriodType === TransactionTimePeriod.FixedMonth && rest.fixedMonth
                  ? rest.fixedMonth.getMonth() + 1
                  : undefined,
              targetYear:
                timePeriodType === TransactionTimePeriod.FixedMonth
                  ? rest.fixedMonth?.getFullYear()
                  : undefined,
              startDate:
                timePeriodType === TransactionTimePeriod.FixedWindow
                  ? rest.fixedWindow?.startDate?.toISOString()
                  : undefined,
              endDate:
                timePeriodType === TransactionTimePeriod.FixedWindow
                  ? rest.fixedWindow?.endDate?.toISOString()
                  : undefined,
              // We remove temporary technical fields to match the schema.
              includeThreshold: undefined,
              type: undefined,
              purchasedLocationUuids: undefined,
              purchasedLocationGroupUuids: undefined,
              fixedWindow: undefined,
              fixedMonth: undefined,
            };
          },
        );
    }
  }

  if (segments?.inAllSegments?.length) {
    const inAllSegmentIds = segments.inAllSegments.map((segment) => segment.uuid);
    _.set(targetingAttributesInput, 'inAllSegmentsTargetingAttribute.segmentIds', inAllSegmentIds);
  }

  if (segments?.inAnySegments?.length) {
    const inAnySegmentsIds = segments.inAnySegments.map((segment) => segment.uuid);
    _.set(targetingAttributesInput, 'inAnySegmentsTargetingAttribute.segmentIds', inAnySegmentsIds);
  }

  if (segments?.notInAnySegments?.length) {
    const notInAnySegmentIds = segments.notInAnySegments.map((segment) => segment.uuid);
    _.set(
      targetingAttributesInput,
      'notInAnySegmentsTargetingAttribute.segmentIds',
      notInAnySegmentIds,
    );
  }

  if (fulfillments?.[0]?.targetOffer) {
    const getFulfillmentTargetingAttribute = () => {
      return fulfillments.map((item) => {
        const { fulfilled, targetOffer, operator, timesFulfilled } = item;
        const isFulfilled = fulfilled === OfferFulfillment.Fulfilled;

        if (isFulfilled) {
          return {
            fulfilled: true,
            targetOfferId: targetOffer?.id,
            operator,
            timesFulfilled,
          };
        }

        return {
          fulfilled: false,
          targetOfferId: targetOffer?.id,
        };
      });
    };
    _.set(
      targetingAttributesInput,
      'offerFulfillmentTargetingAttribute',
      getFulfillmentTargetingAttribute(),
    );
  }

  if (homeStore?.locations?.length || homeStore?.locationGroups?.length) {
    const storeLocationTargeting: AudienceStoreLocationTargetingAttributeInput = {
      locationsIds: homeStore?.locations?.map((location) => location.uuid) ?? [],
      locationGroupUuids:
        homeStore?.locationGroups?.map((locationGroup) => locationGroup.uuid) ?? [],
    };

    _.set(targetingAttributesInput, 'storeLocationTargetingAttribute', storeLocationTargeting);
  }

  if (walletSteeringItems?.[0].wallet) {
    const walletSteeringTargetingItems: AudienceWalletSteeringTargetingAttributeInput[] =
      walletSteeringItems.map((item) => ({
        walletUuid: item.wallet?.uuid as string,
        attribute: item.dimension!,
        walletSteeringOperator: item.operator!,
        value: item.value!,
        unitOfMeasure: item.wallet?.configuration.unitOfMeasure || Unit_Of_Measure.Unit,
      }));

    targetingAttributesInput.walletSteeringTargetingAttribute = walletSteeringTargetingItems;
  }

  if (externalIdentityVerification) {
    const mappedIdentityVerifications = mapExternalIdentityVerificationToArray(
      externalIdentityVerification,
    );
    if (mappedIdentityVerifications.length > 0) {
      targetingAttributesInput.externalIdentityVerificationTargetingAttribute =
        mappedIdentityVerifications;
    }
  }

  if (productInterests?.productCategories?.length) {
    const { prefer, match, productCategories } = productInterests;
    const productCategoriesIds = productCategories.map((category) => category.id);

    const targetingAttributeKey = productInterestsTargetingAttributeMap[prefer][match];
    targetingAttributesInput[targetingAttributeKey] = {
      productCategories: productCategoriesIds,
    };
  }

  const eventsTargetingAttribute = convertQualifiedEventsFormValuesToTargetingAttributeInput(
    qualifiedEvents || [],
  );
  targetingAttributesInput.inAllEventTargetingAttribute =
    eventsTargetingAttribute.inAllEventTargetingAttribute;

  if (surveys?.[0]?.survey) {
    targetingAttributesInput.surveyResponseTargetingAttribute =
      getSurveyResponseTargetingAttributes(surveys);
  }

  if (combinedAudiences?.audiences && combinedAudiences.audiences.length > 0) {
    targetingAttributesInput.aggregatedAudienceTargetingAttribute = {
      target: combinedAudiences.audiences.map((audience) => audience.uuid),
      operator: combinedAudiences.operator,
    };
  }

  const communicationPreferenceCriteria: AudienceCommunicationPreferenceCriteriaInput[] = (
    [
      [Communication_Preference_Mechanism.Sms, communicationPreferences?.sms],
      [Communication_Preference_Mechanism.Push, communicationPreferences?.push],
    ] as const
  ).reduce<AudienceCommunicationPreferenceCriteriaInput[]>((criteria, [mechanism, preference]) => {
    if (preference?.status) {
      criteria.push({
        mechanism,
        status: preference.status,
        subBrandUuid: preference.subBrandUuid || null,
      });
    }
    return criteria;
  }, []);

  if (communicationPreferenceCriteria.length > 0) {
    targetingAttributesInput.communicationPreferenceTargetingAttribute = {
      criteria: communicationPreferenceCriteria,
    };
  }

  return {
    ...targetingAttributesInput,
  };
};

export const isExternalIdentityVerificationConfigured = (
  value: ExternalIdentityVerificationFormValue | null | undefined,
): boolean =>
  !!(
    value &&
    ((value.inAll?.verificationAttributes?.length ?? 0) > 0 ||
      (value.inAny?.verificationAttributes?.length ?? 0) > 0 ||
      (value.notInAll?.verificationAttributes?.length ?? 0) > 0)
  );

export const isAtLeastOneConfigurationPresent = (values: AudienceFormValues): boolean => {
  const hasAgeWithin = Object.values(values.ageWithin || {}).filter(Boolean).length;
  const hasBehaviors = prepareBehaviorsCriteria(values.behaviors).length > 0;
  const hasSegments = _.flatten(Object.values(values.segments || {})).length;
  const hasPurchases = values.purchases?.filter(
    (item) => item.threshold && item.productGroups?.length,
  ).length;
  const hasLastVisit =
    values.lastVisit &&
    typeof values.lastVisit.amount === 'number' &&
    values.lastVisit.period &&
    values.lastVisit.operator;

  const hasPointBalance =
    values.pointBalance &&
    values.pointBalance.operator &&
    typeof values.pointBalance.points === 'number' &&
    values.pointBalance.period;

  const hasMembershipCreated =
    values.membershipCreated &&
    values.membershipCreated.operator &&
    typeof values.membershipCreated.amount === 'number';

  const hasFulfillment = !!values.fulfillments?.[0]?.targetOffer;
  const hasHomeStore =
    !!values?.homeStore?.locations?.length || !!values?.homeStore?.locationGroups?.length;
  const hasWalletSteering = !!values?.walletSteeringItems?.some(
    (item) => item.wallet && item.value !== undefined && item.value !== null,
  );
  const hasACH = !!values?.behaviors?.hasACH;
  const hasProductInterests = !!values.productInterests?.productCategories?.length;
  const hasSurveys = !!values.surveys?.[0]?.survey;
  const hasCombinedAudiences = !!values.combinedAudiences?.audiences?.length;
  const hasCommunicationPreferences = !!(
    values.communicationPreferences?.sms?.status || values.communicationPreferences?.push?.status
  );
  const hasEmployeeStatus = !!values.employeeTargetingAttributes?.retailerNetworkUserType;
  const hasQualifiedEvents = !!values.qualifiedEvents?.some(getIsQualifiedEventsFilled);
  const hasExternalIdentityVerification = isExternalIdentityVerificationConfigured(
    values.externalIdentityVerification,
  );

  const hasConfiguration =
    hasAgeWithin ||
    hasBehaviors ||
    hasSegments ||
    hasPurchases ||
    hasFulfillment ||
    hasHomeStore ||
    hasWalletSteering ||
    hasProductInterests ||
    hasLastVisit ||
    hasPointBalance ||
    hasMembershipCreated ||
    hasSurveys ||
    hasCombinedAudiences ||
    hasACH ||
    hasCommunicationPreferences ||
    hasEmployeeStatus ||
    hasQualifiedEvents ||
    hasExternalIdentityVerification;

  return !!hasConfiguration;
};

export const hasFixedDateRange = (
  targetingAttributes: AudienceTargetingAttributeFragment[],
): boolean => {
  const isFixedDateRange = (
    attributes:
      | (
          | AudiencePurchasedAtLeastTargetingAttributeFragment
          | AudiencePurchasedAtMostTargetingAttributeFragment
        )[]
      | undefined,
  ) => {
    if (attributes) {
      return !!attributes.find((item) => item.startDate && item.endDate);
    }

    return false;
  };

  const audiencePurchasedAtLeastTargetingAttribute = targetingAttributes.filter(
    (item): item is AudiencePurchasedAtLeastTargetingAttributeFragment =>
      item.__typename === 'AudiencePurchasedAtLeastTargetingAttribute',
  );
  const audiencePurchasedAtMostTargetingAttribute = targetingAttributes.filter(
    (item): item is AudiencePurchasedAtMostTargetingAttributeFragment =>
      item.__typename === 'AudiencePurchasedAtMostTargetingAttribute',
  );

  return (
    isFixedDateRange(audiencePurchasedAtLeastTargetingAttribute) ||
    isFixedDateRange(audiencePurchasedAtMostTargetingAttribute)
  );
};

export const isAudienceActiveButNotReady = (
  status?: Audience_Status,
  hasBeenBackFilled?: boolean | null,
  hasFixedDateRange?: boolean,
): boolean => !!(status === Audience_Status.Active && hasFixedDateRange && !hasBeenBackFilled);

export const getStatusLabel = (
  status?: Audience_Status,
  hasBeenBackFilled?: boolean | null,
  hasFixedDateRange?: boolean,
): string => {
  if (isAudienceActiveButNotReady(status, hasBeenBackFilled, hasFixedDateRange)) {
    return 'Active (not ready)';
  }

  return _.capitalize(status || 'No status');
};

const AUDIENCES_BASE_URL = '/audiences';

export const routeCreator = {
  base: () => AUDIENCES_BASE_URL,
  create: () => `${AUDIENCES_BASE_URL}/create`,
  details: (id: string) => `${AUDIENCES_BASE_URL}/${id}`,
  update: (id: string) => `${AUDIENCES_BASE_URL}/${id}/update`,
  export: (id: string, reportId: string) => `${AUDIENCES_BASE_URL}/${id}/export/${reportId}`,
};
export const getFormFieldUIStatusColors = (
  theme: Theme,
  formFieldUIStatus: FormFieldUIStatus,
): IIconStatusColors => {
  const statusMap: Record<FormFieldUIStatus, IIconStatusColors> = {
    error: {
      iconBackgroundColor: lighten(theme.palette.error.main, 0.9),
      iconColor: 'error.main',
    },
    filled: {
      iconBackgroundColor: lighten(theme.palette.primary.main, 0.9),
      iconColor: 'primary.main',
    },
    initial: {
      iconBackgroundColor: grey[100],
      iconColor: grey[500],
    },
  };

  return statusMap[formFieldUIStatus];
};

const getIsQualifiedEventsFilled = (values: QualifiedEventsFormValues | undefined) => {
  if (
    values?.event === QualifiedEventType.FulfilledLoyaltyEvent &&
    hasFilledValuesInLoyaltyEventForm(values)
  ) {
    return true;
  }

  if (values?.event === QualifiedEventType.MadePurchase && hasFilledValuesInPurchaseForm(values)) {
    return true;
  }

  return false;
};

export interface IIconStatusColors {
  iconBackgroundColor: BoxProps['bgcolor'];
  iconColor: string;
}

export type FormFieldUIStatus = 'error' | 'filled' | 'initial';

type IsFieldFilled = (params: { formValues: TargetingAttributesFormValues }) => boolean;

const getIsFieldFilledMap: Record<TargetingAttributesFormField, IsFieldFilled> = {
  purchases: ({ formValues }) => {
    const purchasesItemsValues = formValues.purchases?.[0];
    return !!(
      purchasesItemsValues?.productGroups?.length ||
      purchasesItemsValues?.threshold ||
      purchasesItemsValues?.locations?.length ||
      purchasesItemsValues?.locationGroups?.length
    );
  },
  fulfillments: ({ formValues }) => {
    const fulfillmentItemsValues = formValues.fulfillments?.[0];
    return !!(fulfillmentItemsValues?.targetOffer || fulfillmentItemsValues?.operator);
  },
  behaviors: ({ formValues }) => {
    const behaviorsValues = formValues.behaviors;
    return !!(
      behaviorsValues?.acquisitionChannelAttribute?.length ||
      behaviorsValues?.eaivAttributes?.length ||
      behaviorsValues?.activeSubBrand ||
      behaviorsValues?.hasACH
    );
  },
  communicationPreferences: ({ formValues: { communicationPreferences } }) => {
    return !!(communicationPreferences?.sms?.status || communicationPreferences?.push?.status);
  },
  homeStore: ({ formValues }) => {
    const homeStoreLocations = formValues.homeStore?.locations;
    const homeStoreLocationGroups = formValues.homeStore?.locationGroups;
    return !!homeStoreLocations?.length || !!homeStoreLocationGroups?.length;
  },
  productInterests: ({ formValues }) => {
    const productInterestsValue = formValues.productInterests?.productCategories;
    return !!productInterestsValue?.length;
  },
  walletSteeringItems: ({ formValues }) => {
    const walletSteeringItemsValues = formValues.walletSteeringItems?.[0];
    return !!(walletSteeringItemsValues?.wallet || walletSteeringItemsValues?.value);
  },
  ageWithin: ({ formValues }) => {
    const ageWithinValues = formValues.ageWithin;
    return !!(ageWithinValues?.minimumAge || ageWithinValues?.maximumAge);
  },
  segments: ({ formValues }) => {
    const segmentsValues = formValues.segments;
    return !!(
      segmentsValues?.inAllSegments?.length ||
      segmentsValues?.inAnySegments?.length ||
      segmentsValues?.notInAnySegments?.length
    );
  },
  qualifiedEvents: ({ formValues: { qualifiedEvents } }) => {
    if (!qualifiedEvents) {
      return false;
    }

    return qualifiedEvents.every((formValues) => getIsQualifiedEventsFilled(formValues));
  },
  lastVisit: ({ formValues: { lastVisit } }) => {
    return !!(typeof lastVisit?.amount === 'number' || lastVisit?.operator || lastVisit?.period);
  },
  pointBalance: ({ formValues: { pointBalance } }) => {
    return !!(
      pointBalance?.operator ||
      typeof pointBalance?.points === 'number' ||
      pointBalance?.period
    );
  },
  membershipCreated: ({ formValues: { membershipCreated } }) => {
    return !!(typeof membershipCreated?.amount === 'number' || membershipCreated?.operator);
  },
  employeeTargetingAttributes: ({ formValues: { employeeTargetingAttributes } }) => {
    return !!(employeeTargetingAttributes && employeeTargetingAttributes.retailerNetworkUserType);
  },
  surveys: ({ formValues: { surveys } }) => {
    if (!surveys) {
      return false;
    }

    return surveys.every((formValues) => getIsSurveysSectionFilled(formValues));
  },
  combinedAudiences: ({ formValues: { combinedAudiences } }) => {
    if (!combinedAudiences) {
      return false;
    }

    if (combinedAudiences.audiences && combinedAudiences.audiences.length === 0) {
      return false;
    }

    return true;
  },
  externalIdentityVerification: ({ formValues }) =>
    isExternalIdentityVerificationConfigured(formValues.externalIdentityVerification),
};

export interface IGetIconStatusColorsParams {
  form: FormApi<TargetingAttributesFormValues>;
  fieldName: TargetingAttributesFormField;
  isArray: boolean;
  formValues: TargetingAttributesFormValues;
}

export const getFormFieldUIStatus = ({
  form,
  fieldName,
  isArray,
  formValues,
}: IGetIconStatusColorsParams): FormFieldUIStatus => {
  let isError;
  if (isArray) {
    isError = form.getFieldState(fieldName)?.error?.length > 0;
  } else {
    const registeredFields = form
      .getRegisteredFields()
      .filter((name) => name.startsWith(fieldName));
    isError = !!registeredFields.find(
      (fieldName: TargetingAttributesFormField) => form.getFieldState(fieldName)?.error,
    );
  }

  if (isError) {
    return 'error';
  }

  const getIsFieldFilled = getIsFieldFilledMap[fieldName];
  if (getIsFieldFilled({ formValues })) {
    return 'filled';
  }

  return 'initial';
};

export const formatThreshold = (
  threshold: number,
  countBy: Count_Products_By | null | undefined,
) => {
  if (countBy === Count_Products_By.MoneyAmount) {
    return `$${threshold}`;
  }

  return threshold;
};

export const addCustomIssue = (ctx: z.RefinementCtx, field: string, message = IS_REQUIRED) =>
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: message,
    path: [field],
  });
